<template>
  <KTCodePreview v-bind:title="'Chi tiết phiếu kiểm tồn kho'">
    <template v-slot:preview>
      <!-- Thông tin tóm tắt luôn hiển thị -->
      <div class="card card-custom mb-3">
        <div
          class="card-header cursor-pointer d-flex align-items-center justify-content-between py-3"
          @click="showInfoExpanded = !showInfoExpanded"
        >
          <div class="d-flex align-items-center flex-wrap" style="gap: 16px">
            <span class="font-weight-bold text-dark mr-2">
              <i class="fas fa-info-circle text-primary mr-1"></i>
              {{ stockCheckSheet.code || '—' }}
            </span>
            <span>
              <i class="fas fa-store mr-1"></i>{{ stockCheckSheet.storeName }}
            </span>
            <span>
              <i class="fas fa-box mr-1"></i>{{ stockCheckSheet.productTypeName }}
            </span>
            <StatusLabel
              :status="stockCheckSheet.status || 1"
              :statusName="stockCheckSheet.statusName"
            />
          </div>
          <i
            :class="showInfoExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
            class="text-muted ml-3"
            style="flex-shrink: 0"
          ></i>
        </div>

        <b-collapse v-model="showInfoExpanded">
          <div class="card-body pt-2 pb-3">
            <b-row class="mb-0">
              <b-col lg="4" xl="3" class="pr-lg-4 mb-3 mb-lg-0">
                <b-list-group flush>
                  <b-list-group-item class="border-0 px-0 py-1">
                    <b-row v-if="stockCheckSheet.managerEmployeeName">
                      <b-col class="font-weight-bold text-muted" md="5">QL cửa hàng:</b-col>
                      <b-col>{{ stockCheckSheet.managerEmployeeName }}</b-col>
                    </b-row>
                    <b-row>
                      <b-col class="font-weight-bold text-muted" md="5">Người tạo:</b-col>
                      <b-col>{{ stockCheckSheet.createdBy }}</b-col>
                    </b-row>
                  </b-list-group-item>
                  <b-list-group-item class="border-0 px-0 py-1">
                    <b-row>
                      <b-col class="font-weight-bold text-muted" md="5">Ngày tạo:</b-col>
                      <b-col>{{ stockCheckSheet.createdAt }}</b-col>
                    </b-row>
                  </b-list-group-item>
                  <b-list-group-item class="border-0 px-0 py-1">
                    <b-row>
                      <b-col class="font-weight-bold text-muted" md="5">Cập nhật:</b-col>
                      <b-col>{{ stockCheckSheet.updatedAt }}</b-col>
                    </b-row>
                  </b-list-group-item>
                  <b-list-group-item
                    v-if="stockCheckSheet.stockCheckProductCounts && stockCheckSheet.stockCheckProductCounts.length"
                    class="border-0 px-0 py-1"
                  >
                    <b-row>
                      <b-col class="font-weight-bold text-muted" md="5">Nguồn:</b-col>
                      <b-col>
                        <div
                          v-for="item in stockCheckSheet.stockCheckProductCounts"
                          :key="item.sourceType"
                        >
                          <span v-if="item.sourceType && item.count">
                            {{ getSourceTypeName(item.sourceType) }}:
                            <span class="font-weight-bold text-primary">{{ item.count }}</span>
                          </span>
                        </div>
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
              </b-col>

              <b-col lg="4" xl="5" class="mb-3 mb-lg-0">
                <div class="font-weight-bold text-muted mb-1" style="font-size: 0.85rem">
                  <i class="fas fa-edit text-success mr-1"></i> Ghi chú kiểm kho
                </div>
                <b-form-textarea
                  v-model="stockCheckSheet.note"
                  placeholder="Nhập ghi chú kiểm kho..."
                  rows="5"
                  style="resize: none; font-size: 0.9rem"
                ></b-form-textarea>
              </b-col>

              <b-col lg="4" xl="4">
                <div class="font-weight-bold text-muted mb-1" style="font-size: 0.85rem">
                  <i class="fas fa-exclamation-triangle text-warning mr-1"></i> Danh sách lệch tồn
                </div>
                <b-form-textarea
                  v-model="stockCheckSheet.systemNote"
                  readonly
                  rows="5"
                  style="resize: none; font-size: 0.9rem"
                ></b-form-textarea>
              </b-col>
            </b-row>
          </div>
        </b-collapse>
      </div>
      <InventoryCheckTable
        ref="inventoryCheckTable"
        :stockCheckSheetData="stockCheckSheet"
        @onRefresh="fetchStockCheckSheet"
      >
        <b-button
          @click="saveAllPendingChanges()"
          size="sm"
          style="width: 70px;"
          variant="primary"
          class="font-weight-bolder mr-3"
          v-if="stockCheckSheet.id && stockCheckSheet.status === STOCK_CHECK_SHEET_STATUS.NEW"
        >
          Lưu
        </b-button>
        <b-button
          v-if="
            checkPermissions(['STOCK_CHECK_CONFIRM', 'STOCK_CHECK_MANAGE_ALL']) && stockCheckSheet.status !== STOCK_CHECK_SHEET_STATUS.DONE
          "
          @click="confirmStockCheckSheet()"
          size="sm"
          variant="warning"
          class="font-weight-bolder mr-3"
          :disabled="isConfirmed && !checkPermissions(['STOCK_CHECK_CONFIRM', 'STOCK_CHECK_MANAGE_ALL'])"
        >
          Xác nhận
        </b-button>

        <b-button
          v-if="
            isConfirmed &&
            stockCheckSheet.status === STOCK_CHECK_SHEET_STATUS.STOCK_DIFF &&
            checkPermissions(['STOCK_CHECK_CONFIRM'])
          "
          @click="revertStockCheckSheet()"
          size="sm"
          variant="danger"
          class="font-weight-bolder mr-3"
        >
          <i class="fas fa-undo mr-1"></i>
          Tiếp tục kiểm
        </b-button>
        <b-button
          v-if="stockCheckSheet.id && checkPermissions(['STOCK_CHECK_MANAGE_ALL', 'STOCK_CHECK_CONFIRM'])"
          @click="printItem(stockCheckSheet)"
          size="sm"
          variant="info"
          class="font-weight-bolder mr-3"
        >
          <i style="font-size: 1rem" class="fas fa-print mr-1"></i>
          In phiếu
        </b-button>
        <b-button
          @click="redirectToListPage()"
          size="sm"
          variant="secondary"
          class="font-weight-bolder mr-3"
        >
          Trở về
        </b-button>
      </InventoryCheckTable>
    </template>
  </KTCodePreview>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { formatDate } from '@/utils/common';
import StatusLabel from '@/view/components/inventoryCheck/StatusLabel.vue';
import InventoryCheckTable from '@/view/components/inventoryCheck/InventoryCheckTable.vue';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { STOCK_CHECK_SHEET_STATUS } from '@/utils/enum';
import { makeToastFaile, handleError, makeToastSuccess } from '@/utils/common';
import {
  showConfirmDialog,
  showLoadingDialog,
  closeSwal,
} from '@/utils/sweet-alert2';
import { checkPermission, checkPermissions } from '@/utils/saveDataToLocal';
import { STOCK_CHECK_PRODUCT_LOG_SOURCE_TYPE_NAME } from '@/utils/enum-name';

export default {
  data() {
    return {
      STOCK_CHECK_SHEET_STATUS,
      stockCheckSheet: {
        id: null,
        systemNote: '',
        productType: null,
        note: '',
        categories: [],
        stockCheckProductCounts: [],
      },
      tabIndex: 0,
      isConfirmed: true,
      showInfoExpanded: false,
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Danh sách phiếu kiểm tồn kho', route: '/inventory-check' },
      { title: 'Kiểm tra tồn kho' },
    ]);
  },
  created() {
    this.fetchStockCheckSheet();
  },
  computed: {},
  methods: {
    formatDate,
    checkPermission,
    checkPermissions,
    linkClass(idx) {
      if (this.tabIndex === idx) {
        return ['bg-primary', 'text-light', 'font-weight-bold'];
      } else {
        return ['bg-light', 'text-dark', 'font-weight-bold'];
      }
    },
    fetchStockCheckSheet: function () {
      this.stockCheckSheet.id = Number(this.$route.query.id);
      ApiService.get(`v1/stock-check-sheet/${this.stockCheckSheet.id}`)
        .then((res) => {
          this.stockCheckSheet = res.data.data;
          this.isConfirmed =
            this.stockCheckSheet.status !== STOCK_CHECK_SHEET_STATUS.NEW;
        })
        .catch((err) => {
          handleError(err);
        });
    },
    async saveAllPendingChanges() {
      try {
        await showConfirmDialog(
          'Xác nhận lưu thay đổi?',
          'Bạn có chắc muốn lưu các thay đổi hiện tại?',
        );
      } catch {
        return;
      }
      const tableRef = this.$refs.inventoryCheckTable;
      if (!tableRef) return;
      showLoadingDialog('Đang lưu...', 'Vui lòng chờ...');
      try {
        const success = await tableRef.saveAllPendingChanges();
        if (success) makeToastSuccess('Lưu thành công!');
      } finally {
        closeSwal();
      }
    },
    async confirmStockCheckSheet() {
      try {
        showConfirmDialog(
          'Lưu phiếu kiểm kê?',
          'Bạn có chắc muốn lưu phiếu kiểm kê. Phiếu sau khi được lưu sẽ không thể chỉnh sửa!',
        )
          .then(async () => {
            showLoadingDialog(
              'Thao tác có thể mất vài phút',
              'Vui lòng chờ...',
            );
            const tableRef = this.$refs.inventoryCheckTable;
            if (tableRef) {
              const success = await tableRef.saveAllPendingChanges();
              if (!success) {
                closeSwal();
                return;
              }
            }
            return this.saveStockCheckSheet();
          })
          .catch(() => {});
      } catch (err) {
        const { message } = err.response.data || 'Lỗi!';
        makeToastFaile(message);
      }
    },

    async saveStockCheckSheet() {
      try {
        const dataUpdate = {
          id: this.stockCheckSheet.id,
          note: this.stockCheckSheet.note || '',
        };
        const response = await ApiService.put(
          `v1/stock-check-sheet/${this.stockCheckSheet.id}`,
          dataUpdate,
        );
        const { message } = response.data;
        makeToastSuccess(message);
        this.fetchStockCheckSheet();
      } catch (err) {
        handleError(err);
      } finally {
        closeSwal();
      }
    },
    getSourceTypeName(sourceType) {
      return STOCK_CHECK_PRODUCT_LOG_SOURCE_TYPE_NAME[sourceType];
    },

    async revertStockCheckSheet() {
      showConfirmDialog(
        'Hoàn lại trạng thái phiếu thành mới?',
        'Bạn có chắc muốn hoàn lại trạng thái phiếu kiểm kho về trạng thái mới? Hành động này sẽ cho phép tiếp tục kiểm kho.',
      )
        .then(() => {
          showLoadingDialog('Đang xử lý...', 'Vui lòng chờ...');
          return this.performRevertStockCheckSheet();
        })
        .catch(() => {});
    },
    async performRevertStockCheckSheet() {
      try {
        const response = await ApiService.put(
          `v1/stock-check-sheet/update-status/${this.stockCheckSheet.id}`,
          { status: STOCK_CHECK_SHEET_STATUS.NEW },
        );
        const { message } = response.data;
        makeToastSuccess(message);
        this.fetchStockCheckSheet();
      } catch (err) {
        handleError(err);
      } finally {
        closeSwal();
      }
    },
    redirectToListPage() {
      this.$router.push({
        name: 'list-inventory-check',
      });
    },
    printItem(item) {
      this.$router.push({
        name: 'print-inventory-check',
        query: { id: item.id },
      });
    },
  },
  components: {
    StatusLabel,
    KTCodePreview,
    InventoryCheckTable,
  },
};
</script>

<style>
.note textarea.form-control,
.note {
  height: 100%;
}

.card-custom {
  border: 1px solid #e4e6ea;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-custom .card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0;
}

.card-custom .card-title {
  font-size: 1rem;
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.cursor-pointer:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%) !important;
}
</style>
